import { amICoachingClientOptions } from "@/actions/client-metrics/handler";
import {
  getProgramDayOptions,
  getProgramOptions,
} from "@/actions/program/handler";
import {
  assignProgramFn,
  getActiveUserProgramOptions,
  updateProgramWeekFn,
} from "@/actions/user-program/handler";
import {
  getEffectiveMaxWeightsOptions,
  getLogsForDayOptions,
} from "@/actions/workout/handler";
import { useWorkoutLogMutation } from "@/actions/workout/useWorkoutLogMutation";
import { ProgramActivationWizard } from "@/components/program/ProgramActivationWizard";
import { ProgramSettingsDialog } from "@/components/program/ProgramSettingsDialog";
import {
  CompactWorkoutSummary,
  type ExerciseRowData,
  type SetLogData,
  SmartTrainingTable,
  WorkoutSummary,
} from "@/components/training";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { calculateSmartWeight } from "@/utils/rpe-calculator";
import { seo } from "@/utils/seo";
import {
  getYouTubeEmbedUrl,
  isValidUrl,
  isYouTubeUrl,
  normalizeUrl,
} from "@/utils/video";
import { kgToUnit, unitToKg, useWeightUnit } from "@/utils/weight-unit";
import * as Sentry from "@sentry/tanstackstart-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Play,
  Video,
} from "lucide-react";
import { Suspense, useCallback, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/_app/program/$programId")({
  loader: async ({ context, params }) => {
    const [data, , coachingResult] = await Promise.allSettled([
      context.queryClient.ensureQueryData(getProgramOptions(params.programId)),
      context.queryClient.ensureQueryData(getActiveUserProgramOptions()),
      context.queryClient.ensureQueryData(amICoachingClientOptions()),
    ]);

    if (coachingResult.status === "rejected") {
      Sentry.withScope((scope) => {
        scope.setTag("page", "/program/$programId");
        scope.setTag("loader_step", "amICoachingClient");
        scope.setExtra("loaderError", coachingResult.reason);
        Sentry.captureException(
          coachingResult.reason instanceof Error
            ? coachingResult.reason
            : new Error(
                `amICoachingClient loader hatasi: ${String(coachingResult.reason)}`,
              ),
        );
      });
    }

    const programData = data.status === "fulfilled" ? data.value : undefined;

    const isCoachingClient =
      coachingResult.status === "fulfilled"
        ? ((
            coachingResult.value as
              | { body?: { isCoachingClient?: boolean } }
              | undefined
          )?.body?.isCoachingClient ?? false)
        : false;

    return {
      program: programData,
      isCoachingClient,
    };
  },
  head: ({ loaderData }) => {
    const programData = loaderData?.program;
    const program =
      programData && "body" in programData ? programData.body : null;
    if (!program || "message" in program) return {};

    return {
      meta: seo({
        title: `${program.name} | Antrenman Programı | AtalayTheCoach`,
        description:
          program.description ||
          `${program.name} antrenman programı ile gücünüzü artırın. ${program.durationWeeks} hafta, haftada ${program.daysPerWeek} gün antrenman.`,
        image: program.image || undefined,
        canonical: `https://atalaythecoach.com/program/${program.programId}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Course",
            name: program.name,
            description: program.description,
            provider: {
              "@type": "Organization",
              name: "AtalayTheCoach",
              sameAs: "https://atalaythecoach.com",
            },
          }),
        },
      ],
    };
  },
  component: ProgramDetailPage,
});

function ProgramDetailPage() {
  const { programId } = Route.useParams();

  return (
    <div className="container mx-auto max-w-4xl px-4 pt-24 pb-8">
      {/* Top Navigation & Settings */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <Link
          to="/program"
          className="inline-flex items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Programlara Dön
        </Link>

        <ProgramSettingsDialog />
      </div>

      <Suspense fallback={<ProgramDetailSkeleton />}>
        <ProgramContent programId={programId} />
      </Suspense>
    </div>
  );
}

interface ProgramContentProps {
  programId: string;
}

function ProgramContent({ programId }: ProgramContentProps) {
  const { user } = Route.useRouteContext();
  const { isCoachingClient } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const { unit } = useWeightUnit();

  // Fetch program data
  const { data: programData, isLoading: isProgramLoading } = useQuery(
    getProgramOptions(programId),
  );

  // Fetch user's active program
  const { data: activeProgramData, isLoading: isActiveProgramLoading } =
    useQuery(getActiveUserProgramOptions());

  const activeProgram = activeProgramData?.body;
  const isMyActiveProgram =
    activeProgram &&
    !("message" in activeProgram) &&
    "programId" in activeProgram
      ? activeProgram.programId === programId
      : false;

  const program = programData?.body;

  // Check if we have a valid program (not an error response)
  const isValidProgram = program && "days" in program;
  const days = isValidProgram ? program.days : [];

  // Week and day selection - start from currentWeek if this is user's active program
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Sync selected week when active program data is available
  const hasSyncedInitialWeek = useRef(false);
  if (
    isMyActiveProgram &&
    activeProgram &&
    !("message" in activeProgram) &&
    !hasSyncedInitialWeek.current
  ) {
    setSelectedWeek(activeProgram.currentWeek);
    setSelectedDayIndex(activeProgram.currentDayIndex || 0); // Initialize current day
    hasSyncedInitialWeek.current = true;
  }

  // Update week/day mutation
  const updatePositionMutation = useMutation({
    mutationFn: updateProgramWeekFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    },
  });

  const assignMutation = useMutation({
    mutationFn: assignProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getActiveUserProgramOptions().queryKey,
      });
    },
  });

  const handleAssign = () => {
    if (!program || "message" in program) return;
    assignMutation.mutate({
      data: {
        programId: program.programId,
      },
    });
  };

  // Handle week change - update backend if this is user's active program
  const handleWeekChange = (newWeek: number) => {
    setSelectedWeek(newWeek);
    setSelectedDayIndex(0); // Reset to first day

    // Update backend for active program
    if (isMyActiveProgram && activeProgram && !("message" in activeProgram)) {
      updatePositionMutation.mutate({
        data: {
          userProgramId: activeProgram.userProgramId,
          weekNumber: newWeek,
          currentDayIndex: 0,
        },
      });
    }
  };

  // Handle day change - update backend
  const handleDayChange = (newDayIndex: number) => {
    setSelectedDayIndex(newDayIndex);

    if (isMyActiveProgram && activeProgram && !("message" in activeProgram)) {
      updatePositionMutation.mutate({
        data: {
          userProgramId: activeProgram.userProgramId,
          weekNumber: selectedWeek,
          currentDayIndex: newDayIndex,
        },
      });
    }
  };

  // Filter days for current week
  const weekDays = useMemo(
    () =>
      days.filter((d: { weekNumber: number }) => d.weekNumber === selectedWeek),
    [days, selectedWeek],
  );

  const selectedDay = weekDays[selectedDayIndex] ?? null;

  // Fetch day details with calculated weights
  const { data: dayData, isLoading: isDayLoading } = useQuery({
    ...getProgramDayOptions(selectedDay?.dayId ?? ""),
    enabled: !!selectedDay?.dayId,
  });

  // Extract exercise IDs from day data
  const exerciseIds = useMemo(() => {
    const dayBody = dayData?.body;
    if (!dayBody || "message" in dayBody) return [];
    return [...new Set(dayBody.exercises.map((e) => e.exerciseId))];
  }, [dayData]);

  // Fetch effective max weights (manual or RPE-estimated)
  const { data: effectiveMaxWeightsData } = useQuery(
    getEffectiveMaxWeightsOptions(exerciseIds),
  );

  // Optimistic mutation hook for workout log updates
  const {
    mutate: logUpdate,
    mutateAsync: logUpdateAsync,
    isPending,
  } = useWorkoutLogMutation();

  // Fetch logs for selected day
  // isPending sırasında refetchInterval'ı durdur — optimistic update'leri korur
  const { data: logsData } = useQuery({
    ...getLogsForDayOptions(programId, selectedDay?.dayId ?? ""),
    enabled: !!selectedDay?.dayId,
    refetchInterval: isPending
      ? false
      : (query) => {
          const logs = query.state.data?.body?.logs ?? [];
          const hasProcessing = logs.some(
            (l) =>
              l.videoProcessingStatus === "pending" ||
              l.videoProcessingStatus === "processing",
          );
          return hasProcessing ? 4000 : false;
        },
  });

  // Handle log update from table
  const handleLogUpdate = useCallback(
    (
      exerciseId: string,
      exerciseOrder: number,
      setNumber: number,
      data: Partial<SetLogData>,
    ) => {
      if (!selectedDay) return;

      const payload = { ...data };
      if (payload.completedWeight !== undefined) {
        payload.completedWeight = unitToKg(payload.completedWeight, unit);
      }

      logUpdate({
        programId,
        dayId: selectedDay.dayId,
        exerciseId,
        exerciseOrder,
        setNumber,
        ...payload,
      });
    },
    [logUpdate, programId, selectedDay, unit],
  );

  // Video yükleme için async versiyon — logId döndürür
  const handleLogUpdateAsync = useCallback(
    async (
      exerciseId: string,
      exerciseOrder: number,
      setNumber: number,
      data: Partial<SetLogData>,
    ) => {
      if (!selectedDay) return null;

      const payload = { ...data };
      if (payload.completedWeight !== undefined) {
        payload.completedWeight = unitToKg(payload.completedWeight, unit);
      }

      const result = await logUpdateAsync({
        programId,
        dayId: selectedDay.dayId,
        exerciseId,
        exerciseOrder,
        setNumber,
        ...payload,
      });

      // logId'yi döndür (trim job için gerekli)
      if (result?.status === 200 && result.body && "log" in result.body) {
        return (result.body as { log: { logId: string } }).log?.logId ?? null;
      }
      return null;
    },
    [logUpdateAsync, programId, selectedDay, unit],
  );

  // Transform data for table
  const tableData: ExerciseRowData[] = useMemo(() => {
    const dayBody = dayData?.body;
    if (!dayBody || "message" in dayBody) return [];

    // Use logs from query data
    const logs = logsData?.body?.logs ?? [];

    // Get effective max weights (manual or RPE-estimated)
    const maxWeights = effectiveMaxWeightsData?.body?.maxWeights || [];

    return dayBody.exercises.map((exercise) => {
      // Find logs for this exercise (with order for duplicates)
      const exerciseLogs = logs.filter(
        (l) =>
          l.exerciseId === exercise.exerciseId &&
          l.exerciseOrder === exercise.order,
      );

      // Create set logs
      const setLogs: SetLogData[] = [];
      const exerciseMax = maxWeights.find(
        (m) => m.exerciseId === exercise.exerciseId,
      );

      // Calculate target weight (in standard KG first)
      let targetWeightKg: number | null = null;
      const isEstimated = exerciseMax?.isEstimated ?? false;

      if (exerciseMax) {
        // Priority 1: Direct Percentage
        if (exercise.percentageOf1RM) {
          // Calculate based on percentage (e.g. 100kg * 0.8 = 80kg)
          // Round to nearest 2.5kg
          targetWeightKg =
            Math.round(
              (exerciseMax.maxWeight * (exercise.percentageOf1RM / 100)) / 2.5,
            ) * 2.5;
        }
        // Priority 2: RPE + Reps (Smart Calc)
        else if (exercise.reps && exercise.targetRPE) {
          targetWeightKg = calculateSmartWeight(
            exerciseMax.maxWeight,
            exercise.reps,
            exercise.targetRPE,
          );
        }
      }

      // Convert target weight to user's unit (round to 5 lbs step for LBS or 2.5 for KG)
      const targetWeight = kgToUnit(
        targetWeightKg,
        unit,
        unit === "lbs" ? 5 : 2.5,
      );

      for (let i = 0; i < exercise.sets; i++) {
        const log = exerciseLogs.find((l) => l.setNumber === i + 1);

        setLogs.push({
          logId: log?.logId ?? null,
          setNumber: i + 1,
          targetWeight,
          targetReps: exercise.reps,
          targetRPE: exercise.targetRPE ?? null,
          completedWeight: kgToUnit(log?.completedWeight, unit),
          completedReps: log?.completedReps ?? null,
          completedRPE: log?.completedRPE ?? null,
          notes: log?.notes ?? null,
          videoUrl: log?.videoUrl ?? null,
          videoThumbnailUrl: log?.videoThumbnailUrl ?? null,
          videoProcessingStatus: (log?.videoProcessingStatus ?? null) as
            | "pending"
            | "processing"
            | "done"
            | "error"
            | null,
          videoTrimStart: log?.videoTrimStart ?? null,
          videoTrimEnd: log?.videoTrimEnd ?? null,
        });
      }

      return {
        exercise: {
          ...exercise,
          calculatedTargetWeight: targetWeight,
          isTargetWeightEstimated: isEstimated,
        },
        setLogs,
        exerciseInfo: exercise.exerciseInfo,
      };
    });
  }, [dayData, logsData, effectiveMaxWeightsData, unit]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    let totalSets = 0;
    let completedSets = 0;
    let totalVolume = 0;
    let rpeSum = 0;
    let rpeCount = 0;

    for (const exerciseData of tableData) {
      for (const set of exerciseData.setLogs) {
        totalSets++;
        if (set.completedWeight !== null) {
          completedSets++;
          totalVolume += set.completedWeight * (set.completedReps ?? 1);
        }
        if (set.completedRPE !== null) {
          rpeSum += set.completedRPE;
          rpeCount++;
        }
      }
    }

    return {
      totalSets,
      completedSets,
      totalVolume: Math.round(totalVolume),
      averageRPE: rpeCount > 0 ? rpeSum / rpeCount : null,
    };
  }, [tableData]);

  if (
    isProgramLoading ||
    isActiveProgramLoading ||
    !program ||
    !isValidProgram
  ) {
    return <ProgramDetailSkeleton />;
  }

  const totalWeeks = program.durationWeeks;

  return (
    <div className="space-y-6">
      {/* Program Header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="font-bold text-2xl text-foreground">
                {program.name}
              </h1>
              {program.introVideoUrl && isValidUrl(program.introVideoUrl) && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Play className="h-4 w-4" />
                      Tanıtımı İzle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[95vh] w-[95vw] max-w-3xl overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
                    <DialogHeader className="border-b px-4 py-4 sm:px-6">
                      <div className="flex flex-col gap-1 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="min-w-0 flex-1">
                          <DialogTitle className="truncate font-bold text-foreground text-lg sm:text-xl">
                            Program Tanıtımı
                          </DialogTitle>
                          <p className="mt-0.5 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide sm:text-xs">
                            {program.name}
                          </p>
                        </div>
                        <div className="hidden sm:block">
                          <div className="rounded-full bg-muted px-3 py-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                            Video Rehber
                          </div>
                        </div>
                      </div>
                    </DialogHeader>

                    <div className="relative aspect-video w-full bg-black shadow-inner">
                      {isYouTubeUrl(normalizeUrl(program.introVideoUrl)) ? (
                        <iframe
                          src={getYouTubeEmbedUrl(
                            normalizeUrl(program.introVideoUrl),
                          )}
                          title={`${program.name} tanıtım videosu`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="h-full w-full border-0"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-muted/10 p-8 text-center">
                          <div className="max-w-sm space-y-4">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                              <Video className="h-8 w-8 text-primary" />
                            </div>
                            <p className="font-medium text-muted-foreground text-sm">
                              Video oynatılamıyor. Lütfen harici bağlantıdan
                              izleyin.
                            </p>
                            <Button
                              asChild
                              variant="secondary"
                              size="sm"
                              className="rounded-xl font-bold"
                            >
                              <a
                                href={normalizeUrl(program.introVideoUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2"
                              >
                                Videoyu Aç <ChevronRight className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            {program.description && (
              <p className="mt-1 text-muted-foreground text-sm">
                {program.description}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-2">
        {/* Intro Video removed from main flow - now in Dialog above */}
      </div>

      {!isMyActiveProgram && (
        <Card className="border-border/40 bg-secondary/20 shadow-none sm:rounded-3xl">
          <CardContent className="flex flex-col items-center gap-4 py-6 text-center sm:flex-row sm:justify-between sm:px-8 sm:py-8 sm:text-left">
            <div className="space-y-1">
              <h3 className="font-bold text-foreground text-xl tracking-tight sm:text-2xl">
                {activeProgramData?.body &&
                !("message" in activeProgramData.body) &&
                activeProgramData.body.programId !== program.programId
                  ? "Başka Bir Program Aktif"
                  : "Bu Program Aktif Değil"}
              </h3>
              <p className="max-w-md text-muted-foreground text-sm leading-relaxed">
                {activeProgramData?.body &&
                !("message" in activeProgramData.body) &&
                activeProgramData.body.programId !== program.programId
                  ? `Şu an "${activeProgramData.body.program?.name}" programını uyguluyorsunuz. Bu programa geçerseniz mevcut ilerlemeniz duraklatılacaktır.`
                  : "Bu programı önizlemektesiniz. İlerlemeyi kaydetmek ve ağırlık takibi yapabilmek için programı başlatın."}
              </p>
            </div>
            <ProgramActivationWizard
              programName={program.name}
              isPending={assignMutation.isPending}
              onConfirm={handleAssign}
              hasActiveProgram={
                !!activeProgramData?.body &&
                !("message" in activeProgramData.body)
              }
            >
              <Button size="lg" disabled={assignMutation.isPending}>
                {assignMutation.isPending
                  ? "Başlatılıyor..."
                  : activeProgramData?.body &&
                      !("message" in activeProgramData.body) &&
                      activeProgramData.body.programId !== program.programId
                    ? "Bu Programa Geç"
                    : "Bu Programa Başla"}
              </Button>
            </ProgramActivationWizard>
          </CardContent>
        </Card>
      )}

      {/* Week Controls */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setSelectedWeek((w) => Math.max(1, w - 1))}
          disabled={selectedWeek === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Select
            value={selectedWeek.toString()}
            onValueChange={(v) => handleWeekChange(Number(v))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: totalWeeks }, (_, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  Hafta {i + 1}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={() => setSelectedWeek((w) => Math.min(totalWeeks, w + 1))}
          disabled={selectedWeek === totalWeeks}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {weekDays.length > 0 ? (
        <div className="no-scrollbar flex flex-nowrap gap-2 overflow-x-auto pb-2">
          {weekDays.map(
            (
              day: { dayId: string; dayNumber: number; dayName: string },
              index: number,
            ) => (
              <Button
                key={day.dayId}
                variant={selectedDayIndex === index ? "default" : "outline"}
                size="sm"
                onClick={() => handleDayChange(index)}
                className="shrink-0"
              >
                Gün {day.dayNumber}: {day.dayName}
              </Button>
            ),
          )}
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Bu hafta için henüz gün eklenmemiş.
          </CardContent>
        </Card>
      )}

      {/* Summary - Compact on mobile */}
      {selectedDay && (
        <>
          <div className="md:hidden">
            <CompactWorkoutSummary
              completedSets={summaryStats.completedSets}
              totalSets={summaryStats.totalSets}
            />
          </div>
          <div className="hidden md:block">
            <WorkoutSummary {...summaryStats} unit={unit} />
          </div>
        </>
      )}

      {/* Training Table */}
      {selectedDay && (
        <SmartTrainingTable
          exercises={tableData}
          onLogUpdate={handleLogUpdate}
          onLogUpdateAsync={handleLogUpdateAsync}
          isLoading={isDayLoading}
          userRole={user?.role}
          isCoachingClient={isCoachingClient}
          weekNumber={selectedWeek}
          disabled={!isMyActiveProgram}
          unit={unit}
        />
      )}

      {/* Day Notes */}
      {selectedDay?.notes && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Notlar</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-muted-foreground text-sm">
              {selectedDay.notes}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ProgramDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <div className="flex gap-4">
        <Skeleton className="h-10 w-10" />
        <Skeleton className="h-10 w-[140px]" />
        <Skeleton className="h-10 w-10" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-24" />
      </div>
      <Skeleton className="h-[300px] w-full" />
    </div>
  );
}
